import { Send } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { SaveAndResumeLater, type SaveDraftResult } from "../../components/forms/SaveAndResumeLater";
import { buildSubmissionSummary, type SubmissionSummary } from "../../lib/formSubmissionSummary";
import {
  appendStoredDraftFiles,
  buildResumeUrl,
  getCurrentSectionId,
  getResumeDraftId,
  loadFormProgress,
  restoreFormProgress,
  saveFormProgress,
  type DraftFileMeta,
} from "../../lib/formProgress";
import {
  CheckboxField,
  CheckboxGroupField,
  FormSection,
  RadioGroupField,
  SelectField,
  TextareaField,
  TextField,
  ThankYouState,
  TurnstilePlaceholder,
  printSubmittedForm,
} from "./FormPageShared";
import { FormPortalHeader } from "./FormPortalHeader";
import { memoryMediaVaultSections, memoryMediaVaultSummarySections } from "./formsContent";

type FormErrors = Record<string, string>;

const VAULT_FORM_KEY = "memory-media-vault";
const VAULT_FORM_PATH = "/memory-media-vault";
const VAULT_FORM_TITLE = "Memory Media Vault Form";

const requiredTextFields = ["fullName", "email", "phone", "eventType", "eventName", "eventDate"] as const;
const requiredRadioFields = ["eventTiming", "connectedService", "vaultPurpose", "mediaContributors", "vaultAccess"] as const;
const requiredCheckboxFields = [
  "accurateConfirmation",
  "contentPermission",
  "premiumEnhancementConfirmation",
  "changeRequestAcknowledgement",
] as const;

const requiredLabels: Record<string, string> = {
  fullName: "Full Name is required.",
  email: "Email Address is required.",
  phone: "Phone Number is required.",
  eventType: "Event Type is required.",
  eventName: "Event Name or Honoree Name is required.",
  eventDate: "Event Date is required.",
  eventTiming: "Please select whether this is for an upcoming event, past event, or ongoing collection.",
  connectedService: "Please select which Kreatethedate service this Memory Media Vault is connected to.",
  vaultPurpose: "Please select the main purpose of your Memory Media Vault.",
  vaultPurposeOther: "Please describe the main purpose of your Memory Media Vault.",
  mediaContributors: "Please select who will be adding photos or videos.",
  vaultAccess: "Please select how the Memory Media Vault should be accessed.",
  mediaTypes: "Please select at least one type of media you expect to collect.",
  mediaTypesOther: "Please describe the other media type you expect to collect.",
  accurateConfirmation: "Please confirm that the information provided is accurate.",
  contentPermission: "Please confirm that you have permission to submit, collect, and use the provided media.",
  premiumEnhancementConfirmation: "Please confirm that you understand the Memory Media Vault is a premium enhancement.",
  changeRequestAcknowledgement: "Please confirm that you understand major setup changes may require additional time and/or fees.",
};

const contactOptions = ["Email", "Phone", "Text"];
const eventTimingOptions = ["Upcoming event", "Past event", "Ongoing collection"];
const connectedServiceOptions = [
  "Signature Memory Hub",
  "Legacy Memory Hub",
  "Memory Hub upgrade request",
  "Archive Hub",
  "Not sure yet",
];
const vaultPurposeOptions = [
  "Collect photos and videos from guests",
  "Organize family/event media",
  "Preserve memories long-term",
  "Prepare media for a Memory Hub",
  "Prepare media for an Archive Hub",
  "Other",
];
const mediaContributorOptions = [
  "Only me",
  "Family members",
  "Event guests",
  "Organization/team members",
  "Multiple contributors",
  "Not sure yet",
];
const preferenceOptions = ["Yes", "No", "Not sure yet"];
const organizationPreferenceOptions = [
  "By contributor/person",
  "By event moment",
  "By date",
  "By category",
  "Let Kreatethedate recommend",
  "Not sure yet",
];
const reviewPreferenceOptions = [
  "Yes, I want to review first",
  "No, Kreatethedate can organize/use approved media as needed",
  "I would like Kreatethedate to help decide",
  "Not sure yet",
];
const vaultAccessOptions = [
  "Private upload/access link",
  "Shared with selected family/guests",
  "Password-protected if available",
  "Not sure yet",
];
const mediaTypeOptions = ["Photos", "Videos", "Written memories/messages", "Audio files", "Documents/programs/flyers", "Other"];
const futureServicesOptions = [
  "RSVP Hub",
  "Memory Hub",
  "Archive Hub",
  "Signature Song",
  "Signature Highlight Reel",
  "Not at this time",
];

export function MemoryMediaVaultFormPage() {
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState<SubmissionSummary | null>(null);
  const [draftId, setDraftId] = useState("");
  const [restoreNotice, setRestoreNotice] = useState("");
  const [restoredFiles, setRestoredFiles] = useState<DraftFileMeta[]>([]);
  const [showVaultPurposeOther, setShowVaultPurposeOther] = useState(false);
  const [showMediaTypesOther, setShowMediaTypesOther] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    document.title = "Memory Media Vault Form | Kreatethedate";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Complete the Kreatethedate Memory Media Vault Form with event details, media needs, contributors, privacy, and storage goals.",
      );
  }, []);

  useEffect(() => {
    const resumeDraftId = getResumeDraftId();
    if (!resumeDraftId) {
      const targetSectionId = window.location.hash.replace("#", "");
      if (targetSectionId) {
        window.setTimeout(() => {
          document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
        }, 80);
      } else {
        window.scrollTo({ top: 0, left: 0 });
      }
      return;
    }

    const draft = loadFormProgress(VAULT_FORM_KEY, resumeDraftId);
    if (!draft || !formRef.current) {
      setRestoreNotice("We could not find saved progress for this resume link in this browser.");
      return;
    }

    restoreFormProgress(formRef.current, draft);
    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    setRestoreNotice("Your saved progress has been restored in this browser.");
    setShowVaultPurposeOther(draft.fields.vaultPurpose === "Other");
    setShowMediaTypesOther(Array.isArray(draft.fields.mediaTypes) && draft.fields.mediaTypes.includes("Other"));

    const targetSectionId = window.location.hash.replace("#", "") || draft.lastSectionId || memoryMediaVaultSections[0]?.id;
    window.setTimeout(() => {
      if (formRef.current) restoreFormProgress(formRef.current, draft);
      document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
    }, 80);
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set("formType", VAULT_FORM_KEY);
    formData.set("cf-turnstile-response", "turnstile-placeholder");
    if (draftId) await appendStoredDraftFiles(formData, VAULT_FORM_KEY, draftId);

    const nextErrors = validateMemoryMediaVaultForm(formData);
    setErrors(nextErrors);

    const firstInvalidName = Object.keys(nextErrors)[0];
    if (firstInvalidName) {
      setSubmitError("Please complete all required fields before submitting your Memory Media Vault Form.");
      form.querySelector<HTMLElement>(`[name="${firstInvalidName}"]`)?.focus();
      return;
    }

    const summary = buildSubmissionSummary({
      formData,
      formTitle: "Memory Media Vault Setup Form",
      clientNameField: "fullName",
      eventNameField: "eventName",
      eventDateField: "eventDate",
      sections: memoryMediaVaultSummarySections,
    });

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/forms/memory-media-vault", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const message = await response.text();
        if (!(import.meta.env.DEV && response.status === 404)) {
          throw new Error(message || "Your form could not be submitted. Please try again.");
        }
      }

      const contentType = response.headers.get("Content-Type") || "";
      if (response.ok && !contentType.includes("application/json") && !import.meta.env.DEV) {
        throw new Error("The Memory Media Vault submission endpoint is not configured yet.");
      }

      setSubmittedSummary(summary);
      setSubmitted(true);
      formRef.current?.reset();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Your form could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSaveProgress(): Promise<SaveDraftResult> {
    if (!formRef.current) throw new Error("The form is not ready yet.");

    const lastSectionId = getCurrentSectionId(memoryMediaVaultSections.map((section) => section.id));
    const draft = await saveFormProgress({
      form: formRef.current,
      formKey: VAULT_FORM_KEY,
      draftId,
      lastSectionId,
    });
    const resumeLink = buildResumeUrl(VAULT_FORM_PATH, draft.draftId, lastSectionId);

    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    return { draft, resumeLink };
  }

  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <a
        href="#memory-media-vault-form"
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#050505] focus:px-4 focus:py-3 focus:text-[#FF6A2B]"
      >
        Skip to Memory Media Vault form
      </a>
      <FormPortalHeader />

      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18">
        <img src="/assets/celebration-table.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,5,5,.94),rgba(5,5,5,.74)_54%,rgba(5,5,5,.48)),linear-gradient(0deg,rgba(5,5,5,.42),rgba(5,5,5,.14))]" />
        <div className="relative mx-auto grid min-h-[46dvh] max-w-7xl items-end gap-8 px-4 pb-10 pt-24 text-[#fff9ef] sm:px-6 lg:grid-cols-[1fr_.72fr] lg:px-8 lg:pb-14">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#FF6A2B]">Memory Media Vault Form</p>
            <h1 className="mt-5 max-w-4xl text-balance font-display text-5xl font-bold leading-[0.98] sm:text-6xl">
              Let&apos;s Set Up Your Memory Media Vault
            </h1>
          </div>
          <p className="max-w-xl border-l-2 border-[#FF6A2B] pl-4 text-sm font-semibold leading-7 text-[#fff9ef]/76 lg:ml-auto">
            Share your event details, media needs, contributor preferences, and storage goals so we can create an organized
            Memory Media Vault experience for your project.
          </p>
        </div>
      </section>

      {submitted ? (
        <ThankYouState
          message="Your Kreatethedate Memory Media Vault Form has been received. We will review your details and follow up with next steps."
          onDownloadPdf={() => printSubmittedForm(submittedSummary)}
          onPrint={() => printSubmittedForm(submittedSummary)}
          summary={submittedSummary}
        />
      ) : (
        <section className="px-4 pb-28 pt-10 sm:px-6 lg:px-8 lg:pb-28 lg:pt-14">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[290px_1fr]">
            <aside className="hidden h-fit rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] lg:sticky lg:top-24 lg:block">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">Form sections</p>
              <ol className="mt-4 grid gap-2">
                {memoryMediaVaultSections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="focus-ring group grid grid-cols-[34px_1fr] items-center gap-3 rounded-lg px-2 py-2 text-sm font-bold text-[#5c5246] transition hover:bg-[#fff9ef] hover:text-[#050505]"
                    >
                      <span className="grid size-8 place-items-center rounded-full border border-[#FF6A2B]/45 bg-[#050505] text-xs text-[#FF6A2B]">
                        {index + 1}
                      </span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <form id="memory-media-vault-form" ref={formRef} noValidate onSubmit={handleSubmit} className="grid gap-6">
              <SaveAndResumeLater
                formTitle={VAULT_FORM_TITLE}
                getDefaultEmail={() => {
                  const emailControl = formRef.current?.elements.namedItem("email");
                  return emailControl instanceof HTMLInputElement ? emailControl.value : "";
                }}
                onSaveDraft={handleSaveProgress}
                restoreNotice={restoreNotice}
                savedFiles={restoredFiles}
              />
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Estimated completion time: 10&ndash;15 minutes. You can save your progress and return at any time.
              </p>
              <div className="grid gap-3 rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                <p>
                  The Memory Media Vault is a premium enhancement available with eligible Memory Hub services, especially
                  Signature Memory Hub or Legacy Memory Hub.
                </p>
                <p>It is not included with every Memory Hub package.</p>
              </div>
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Required fields are marked with <span className="font-black text-[#D94A16]">*</span>.
              </p>
              {submitError ? (
                <p className="rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800" role="alert">
                  {submitError}
                </p>
              ) : null}

              <FormSection id="contact" number="01" title="Client Contact Information" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <TextField name="fullName" label="Full Name" autoComplete="name" required error={errors.fullName} />
                    <TextField name="email" label="Email Address" type="email" autoComplete="email" required error={errors.email} />
                    <TextField name="phone" label="Phone Number" type="tel" autoComplete="tel" required error={errors.phone} />
                    <SelectField name="preferredContactMethod" label="Preferred Contact Method" options={contactOptions} />
                  </div>
                  <div className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                    <div>
                      <h3 className="font-display text-2xl font-bold text-[#050505]">Additional Event Contact (Optional)</h3>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#5c5246]">
                        Provide an additional contact person if someone else is helping coordinate the event.
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <TextField name="additionalContactName" label="Name" autoComplete="name" />
                      <TextField name="additionalContactPhone" label="Phone Number" type="tel" autoComplete="tel" />
                      <TextField name="additionalContactEmail" label="Email Address" type="email" autoComplete="email" />
                    </div>
                  </div>
                </div>
              </FormSection>

              <FormSection id="event-project-details" number="02" title="Event / Project Details" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="eventType" label="Event Type" required error={errors.eventType} />
                  <TextField name="eventName" label="Event Name or Honoree Name" required error={errors.eventName} />
                  <TextField name="eventDate" label="Event Date" type="date" required error={errors.eventDate} />
                  <RadioGroupField
                    name="eventTiming"
                    label="Is this for an upcoming event, past event, or ongoing collection?"
                    options={eventTimingOptions}
                    required
                    error={errors.eventTiming}
                    className="md:col-span-2"
                  />
                  <TextField name="eventLocation" label="Event Location" className="md:col-span-2" />
                  <TextareaField name="projectDescription" label="Short project description" rows={5} className="md:col-span-2" />
                </div>
              </FormSection>

              <FormSection id="eligibility" number="03" title="Memory Media Vault Eligibility" formName={VAULT_FORM_TITLE}>
                <RadioGroupField
                  name="connectedService"
                  label="Which Kreatethedate service is this Memory Media Vault connected to?"
                  helperText="Memory Media Vault access may depend on the service or package selected."
                  options={connectedServiceOptions}
                  required
                  error={errors.connectedService}
                />
              </FormSection>

              <FormSection id="vault-purpose" number="04" title="Vault Purpose" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="vaultPurpose"
                    label="What is the main purpose of your Memory Media Vault?"
                    options={vaultPurposeOptions}
                    required
                    error={errors.vaultPurpose}
                    onChange={(value) => setShowVaultPurposeOther(value === "Other")}
                  />
                  {showVaultPurposeOther ? (
                    <TextField name="vaultPurposeOther" label="Please describe." required error={errors.vaultPurposeOther} />
                  ) : null}
                </div>
              </FormSection>

              <FormSection id="media-contributors" number="05" title="Media Contributors" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="mediaContributors"
                    label="Who will be adding photos or videos?"
                    options={mediaContributorOptions}
                    required
                    error={errors.mediaContributors}
                  />
                  <RadioGroupField name="simpleUploadLink" label="Do contributors need a simple upload link?" options={preferenceOptions} />
                </div>
              </FormSection>

              <FormSection id="organization-preferences" number="06" title="Media Organization Preferences" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="organizationPreference"
                    label="How would you like uploaded media organized?"
                    options={organizationPreferenceOptions}
                  />
                  <TextareaField name="organizationInstructions" label="Additional organization instructions." rows={6} />
                </div>
              </FormSection>

              <FormSection id="review-approval" number="07" title="Review & Approval Preferences" formName={VAULT_FORM_TITLE}>
                <RadioGroupField
                  name="reviewPreference"
                  label="Would you like to review uploaded media before anything is used publicly?"
                  options={reviewPreferenceOptions}
                />
              </FormSection>

              <FormSection id="privacy-sharing" number="08" title="Privacy & Sharing" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4">
                  <RadioGroupField
                    name="vaultAccess"
                    label="How should the Memory Media Vault be accessed?"
                    options={vaultAccessOptions}
                    required
                    error={errors.vaultAccess}
                  />
                  <TextareaField name="privacyInstructions" label="Additional privacy or sharing instructions." rows={6} />
                </div>
              </FormSection>

              <FormSection id="media-types" number="09" title="Media Types" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4">
                  <p className="rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    Kreatethedate will provide separate upload instructions if media files are needed.
                  </p>
                  <CheckboxGroupField
                    name="mediaTypes"
                    label="What types of media do you expect to collect?"
                    options={mediaTypeOptions}
                    required
                    error={errors.mediaTypes}
                    onChange={(option, checked) => {
                      if (option === "Other") setShowMediaTypesOther(checked);
                    }}
                  />
                  {showMediaTypesOther ? <TextField name="mediaTypesOther" label="Please describe." required error={errors.mediaTypesOther} /> : null}
                </div>
              </FormSection>

              <FormSection id="creative-ideas" number="10" title="Creative Ideas" formName={VAULT_FORM_TITLE}>
                <TextareaField
                  name="creativeIdeas"
                  label="Any Creative Ideas You Would Like Kreatethedate to Consider?"
                  helperText="Share any ideas for how you would like your collected memories organized, presented, preserved, or later used in your Memory Hub or Archive Hub."
                  rows={8}
                />
              </FormSection>

              <FormSection id="future-services" number="11" title="Future Services Interest" formName={VAULT_FORM_TITLE}>
                <CheckboxGroupField
                  name="futureServicesInterest"
                  label="Would you like information about additional Kreatethedate services?"
                  options={futureServicesOptions}
                />
              </FormSection>

              <FormSection id="confirmation" number="12" title="Permission & Confirmation" formName={VAULT_FORM_TITLE}>
                <div className="grid gap-4">
                  <CheckboxField
                    name="accurateConfirmation"
                    label="I confirm that the information provided is accurate to the best of my knowledge."
                    required
                    error={errors.accurateConfirmation}
                  />
                  <CheckboxField
                    name="contentPermission"
                    label="I confirm that I have permission to submit, collect, and use any photos, videos, messages, files, or media provided for this project."
                    required
                    error={errors.contentPermission}
                  />
                  <CheckboxField
                    name="premiumEnhancementConfirmation"
                    label="I understand the Memory Media Vault is a premium enhancement and may depend on the service/package selected."
                    required
                    error={errors.premiumEnhancementConfirmation}
                  />
                  <CheckboxField
                    name="changeRequestAcknowledgement"
                    label="I understand that major changes requested after setup has started may require additional time and/or additional fees."
                    required
                    error={errors.changeRequestAcknowledgement}
                  />
                  <TurnstilePlaceholder />
                </div>
              </FormSection>

              <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                <div className="grid gap-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Memory Media Vault Form</p>
                    <h2 className="mt-1 font-display text-2xl font-bold text-[#050505]">Change Request Notice</h2>
                  </div>
                  <div className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    <p>
                      Please review your information carefully before submitting. Kreatethedate will use the information
                      provided to set up your Memory Media Vault. Major changes requested after setup has begun may affect
                      the project timeline and may require additional work, revisions, or fees.
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex flex-col gap-3 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold leading-7 text-[#5c5246]">
                  Please review your details before submitting your Memory Media Vault form.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-6 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting..." : "Submit Memory Media Vault Form"}
                  <Send className="size-4" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}

function validateMemoryMediaVaultForm(formData: FormData) {
  const nextErrors: FormErrors = {};

  for (const name of requiredTextFields) {
    const value = String(formData.get(name) || "").trim();
    if (!value) nextErrors[name] = requiredLabels[name];
  }

  for (const name of requiredRadioFields) {
    const value = String(formData.get(name) || "").trim();
    if (!value) nextErrors[name] = requiredLabels[name];
  }

  if (String(formData.get("vaultPurpose") || "").trim() === "Other" && !String(formData.get("vaultPurposeOther") || "").trim()) {
    nextErrors.vaultPurposeOther = requiredLabels.vaultPurposeOther;
  }

  const selectedMediaTypes = formData
    .getAll("mediaTypes")
    .filter((entry): entry is string => typeof entry === "string")
    .map((value) => value.trim())
    .filter(Boolean);

  if (!selectedMediaTypes.length) nextErrors.mediaTypes = requiredLabels.mediaTypes;

  if (selectedMediaTypes.includes("Other") && !String(formData.get("mediaTypesOther") || "").trim()) {
    nextErrors.mediaTypesOther = requiredLabels.mediaTypesOther;
  }

  const email = String(formData.get("email") || "").trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    nextErrors.email = "Enter a valid email address.";
  }

  for (const name of requiredCheckboxFields) {
    if (!formData.get(name)) nextErrors[name] = requiredLabels[name];
  }

  return nextErrors;
}
